import copy from 'copy-to-clipboard';
import { capitalize } from 'lodash';

import { MixpanelTrackEventFnType } from '@project44/ui-utils';

import { Identifiers } from '../../definitions/types/shipment';
import { scopedT } from '../../utils/helpers/scopedT';

interface CopyHandlerProps<T> {
  location: 'order' | 'shipment';
  itemType: string;
  toStringParser: (data: T[]) => string;
  trackFn: MixpanelTrackEventFnType;
  dataToCopy: T[];
}

export interface CopyHandlerOutput {
  copySingleItem: (id: string) => void;
  copyAllItems: () => void;
}

const t = scopedT();

export const copyhandler = <T>({
  itemType,
  location,
  toStringParser,
  dataToCopy,
  trackFn,
}: CopyHandlerProps<T>): CopyHandlerOutput => {
  const eventName =
    location === 'shipment'
      ? `MVMT_SHIPMENT_ENTITY_COPY_OVERVIEW_DATA`
      : `MVMT_ORDER_ENTITY_COPY_OVERVIEW_DATA`;

  const allData = toStringParser(dataToCopy);

  return {
    copySingleItem: (data: string) => {
      copy(data);
      trackFn({
        eventName,
        properties: { infoType: itemType, copyType: 'single' },
      });
    },
    copyAllItems: () => {
      copy(allData);
      trackFn({
        eventName,
        properties: { infoType: itemType, copyType: 'multiple' },
      });
    },
  };
};

const additionalIdentifierDictonary: Record<string, string> = {
  AIR_WAYBILL: t('apiMapping.identifier.airWaybill'),
  BILL_OF_LADING: t('apiMapping.identifier.billOfLading'),
  BOOKING_NUMBER: t('apiMapping.identifier.bookingNumber'),
  CONTAINER_ID: t('apiMapping.identifier.containerId'),
  HOUSE_AIR_WAYBILL: t('apiMapping.identifier.houseAirWaybill'),
  ORDER: t('apiMapping.identifier.order'),
  PRO: t('apiMapping.identifier.pro'),
  RAIL_CAR_ID: t('apiMapping.identifier.railCarId'),
  TRACKING_NUMBER: t('apiMapping.identifier.trackingNumber'),
  TRAIN_NUMBER: t('apiMapping.identifier.trainNumber'),
  WAGON_ID: t('apiMapping.identifier.wagonId'),
  WAYBILL: t('apiMapping.identifier.waybill'),
  SERIAL_NUMBER: t('apiMapping.identifier.serialNumber'),
  VEHICLE_IDENTIFICATION_NUMBER: t(
    'apiMapping.identifier.vehicleIdentificationNumber',
  ),
};

type FormattedAdditionalIdentifiers = {
  name: string;
  values: string[];
};

export const formatAdditionalIdentifiers = (
  additionalIdentifiers: Identifiers[],
) => {
  if (additionalIdentifiers.length === 0) return [];

  return additionalIdentifiers
    .reduce((acc, identifier) => {
      const currentIdentifierIndex = acc.findIndex(
        (i: FormattedAdditionalIdentifiers) => i.name === identifier.type,
      );

      if (currentIdentifierIndex !== -1) {
        acc[currentIdentifierIndex].values.push(identifier.value);
      } else {
        acc.push({
          name: identifier.type,
          values: [identifier.value],
        });
      }

      return acc;
    }, [] as FormattedAdditionalIdentifiers[])
    .map((item: FormattedAdditionalIdentifiers) => {
      const name = additionalIdentifierDictonary[item.name];

      return {
        ...item,
        name:
          name || capitalize(item.name.toLocaleLowerCase().replace(/_/g, ' ')),
      };
    });
};
